import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import {
  Check,
  X,
  Shield,
  FileText,
  Zap,
  Users,
  Download,
  Lock,
  Award,
  Sparkles
} from "lucide-react";
import LaurelStats from "@/components/LaurelStats";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MintSlipLogo from "../assests/mintslip-logo.png";

// Same reveal hook the home page uses (tiny threshold so tall sections
// still trigger on mobile).
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

const toneCard = {
  mint: { bg: "bg-emerald-400", big: "text-slate-900", small: "text-emerald-950" },
  black: { bg: "bg-[#0b0b0b]", big: "text-white", small: "text-slate-300" },
  gray: { bg: "bg-slate-100", big: "text-slate-900", small: "text-slate-600" },
};

export default function ComparisonPage() {
  const navigate = useNavigate();
  const [heroRef, heroInView] = useInView();
  const [featuresRef, featuresInView] = useInView();
  const [tableRef, tableInView] = useInView();
  const [cardsRef, cardsInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  // [full label, short mobile label, MintSlip value, Others value] — the
  // short label keeps every capability on ONE line in the mobile table.
  const comparisonFeatures = [
    { name: "Instant PDF Download", short: "Instant PDF Download", mintslip: true, others: "Some" },
    { name: "No Registration Required", short: "No Registration", mintslip: true, others: "Some" },
    { name: "Professional Templates", short: "Templates", mintslip: true, others: "Generic" },
    { name: "Real-Time Preview", short: "Real-Time Preview", mintslip: true, others: true },
    { name: "Mobile Friendly", short: "Mobile Friendly", mintslip: true, others: "Limited" },
    { name: "Secure Payment (Stripe)", short: "Secure Payment", mintslip: true, others: "Varies" },
    { name: "No Data Storage", short: "No Data Storage", mintslip: true, others: false },
    { name: "Auto Tax Calculations", short: "Auto Tax Calcs", mintslip: true, others: "Some" },
    { name: "YTD Calculations", short: "YTD Calculations", mintslip: true, others: false },
    { name: "Customer Support", short: "Customer Support", mintslip: true, others: "Limited" },
    { name: "Contractor/1099 Support", short: "1099 Support", mintslip: true, others: "Some" },
    { name: "Canadian Paystubs", short: "Canadian Paystubs", mintslip: true, others: false },
  ];

  const highlights = [
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Generate professional paystubs in under 2 minutes. No waiting, no delays.",
      tone: "gray",
    },
    {
      icon: Shield,
      title: "100% Secure",
      description: "We don't store your personal data. Your privacy is our priority.",
      tone: "mint",
    },
    {
      icon: Award,
      title: "Professional Quality",
      description: "Industry-standard templates designed for clarity and accuracy.",
      tone: "black",
    },
    {
      icon: Users,
      title: "No Sign-Up",
      description: "Start generating immediately without creating an account.",
      tone: "gray",
    }
  ];

  const differentCards = [
    {
      icon: Zap,
      title: "Blazing Fast",
      description: "Generate documents in under 2 minutes",
      tone: "gray",
    },
    {
      icon: Download,
      title: "Instant Download",
      description: "Get your PDF immediately after creation",
      tone: "mint",
    },
    {
      icon: Lock,
      title: "No Data Stored",
      description: "Your information stays private",
      tone: "black",
    },
  ];

  // Home page's compare-table cell treatments — green shine pill for yes,
  // muted X / small text for the Others column.
  const renderMintCell = (v) =>
    v === true ? (
      <span
        className="cta-shine inline-flex items-center justify-center min-w-[56px] md:min-w-[96px] h-8 md:h-9 px-3 md:px-5 rounded-full text-white"
        style={{ background: "linear-gradient(180deg, #34d399, #10b981)", boxShadow: "0 8px 20px rgba(16, 185, 129, 0.38), inset 0 1px 0 rgba(255, 255, 255, 0.4)" }}
        aria-label="Yes"
      >
        <Check className="w-4 h-4 md:w-5 md:h-5" strokeWidth={3} />
      </span>
    ) : (
      <span className="text-[12.5px] md:text-sm font-semibold text-emerald-600">{v}</span>
    );

  const renderOthersCell = (v) =>
    v === true ? (
      <Check className="w-4 h-4 md:w-5 md:h-5 text-slate-400 inline" strokeWidth={2.5} aria-label="Yes" />
    ) : v === false ? (
      <X className="w-4 h-4 md:w-5 md:h-5 text-slate-300 inline" strokeWidth={2.5} aria-label="No" />
    ) : (
      <span className="text-[12.5px] md:text-sm text-slate-500">{v}</span>
    );

  return (
    <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Helmet>
        <title>MintSlip vs Other Paystub Generators | Feature Comparison</title>
        <meta name="description" content="Compare MintSlip with other paystub generators. See why MintSlip offers faster generation, more templates, better privacy, and professional quality documents." />
        <meta name="keywords" content="paystub generator comparison, best paystub generator, MintSlip vs competitors, paystub maker comparison" />
        <meta property="og:title" content="MintSlip vs Other Paystub Generators" />
        <meta property="og:description" content="See why thousands choose MintSlip for professional paystub generation. Compare features, speed, and quality." />
        <link rel="canonical" href="https://mintslip.com/mintslip-vs-other-paystub-generators" />
      </Helmet>

      <Header title="MintSlip" />

      {/* Hero */}
      <section ref={heroRef} className="relative max-w-[1288px] mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-20">
        <div className={`transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full border border-emerald-100">
              <Sparkles className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">Feature Comparison</span>
            </div>
          </div>

          <h1 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            <span className="font-black">MintSlip</span> vs other paystub generators
          </h1>

          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            See why thousands of users choose MintSlip for their professional document needs. Compare features, speed, and quality side by side.
          </p>

          <div className="flex justify-center mb-5">
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create a Paystub</span>
            </button>
          </div>

          {/* Laurel emblem with the rotating highlights — same as the home page */}
          <LaurelStats className="mt-2" />
        </div>
      </section>

      {/* Feature highlights */}
      <section ref={featuresRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-12 transition-all duration-700 ${featuresInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Why MintSlip <span className="font-black">stands out</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {highlights.map((item, i) => {
            const t = toneCard[item.tone];
            return (
              <div
                key={item.title}
                className={`rounded-[28px] ${t.bg} p-8 md:p-10 transition-all duration-700 ${featuresInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: `${150 + i * 120}ms` }}
              >
                <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
                  <item.icon className="w-7 h-7 text-slate-900" />
                </span>
                <h3 className={`font-display text-xl md:text-2xl font-medium mb-2 ${t.big}`}>{item.title}</h3>
                <p className={`leading-relaxed ${t.small}`}>{item.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Comparison table — same treatment as the home page's compare table */}
      <section ref={tableRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${tableInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Feature <span className="font-black">comparison</span>
        </h2>
        <p className={`text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${tableInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          See how MintSlip compares to typical paystub generators
        </p>

        <div className={`transition-all duration-700 delay-200 ${tableInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="rounded-[20px] border border-slate-200 overflow-hidden bg-white max-w-4xl mx-auto">
            <table className="w-full border-collapse text-[15px]">
              <thead>
                <tr>
                  <th className="px-2 md:px-6 py-3.5 md:py-4 text-left bg-slate-50 border-b border-slate-200 text-[11px] md:text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Feature</th>
                  <th className="px-1 md:px-6 py-3.5 md:py-4 text-center bg-slate-50 border-b border-slate-200 w-[86px] md:w-[150px]">
                    <img src={MintSlipLogo} alt="MintSlip" className="h-[18px] md:h-6 w-auto inline-block align-middle" />
                  </th>
                  <th className="px-2 md:px-6 py-3.5 md:py-4 text-center bg-slate-50 border-b border-slate-200 w-[70px] md:w-[150px] text-[11px] md:text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Others</th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((f, i) => {
                  const bb = i === comparisonFeatures.length - 1 ? "" : " border-b border-slate-200";
                  return (
                    <tr key={f.name}>
                      <td className={`px-2 md:px-6 py-5 md:py-4 text-slate-800 text-[13px] md:text-[15px] whitespace-nowrap${bb}`}>
                        <span className="hidden md:inline">{f.name}</span>
                        <span className="md:hidden">{f.short}</span>
                      </td>
                      <td className={`px-1 md:px-6 py-5 md:py-4 text-center${bb}`}>{renderMintCell(f.mintslip)}</td>
                      <td className={`px-2 md:px-6 py-5 md:py-4 text-center${bb}`}>{renderOthersCell(f.others)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* What makes us different */}
      <section ref={cardsRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-12 transition-all duration-700 ${cardsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          What makes us <span className="font-black">different</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {differentCards.map((c, i) => {
            const t = toneCard[c.tone];
            return (
              <div
                key={c.title}
                className={`rounded-[28px] ${t.bg} p-8 md:p-10 transition-all duration-700 ${cardsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: `${150 + i * 120}ms` }}
              >
                <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
                  <c.icon className="w-7 h-7 text-slate-900" />
                </span>
                <h3 className={`font-display text-2xl md:text-3xl font-medium mb-3 ${t.big}`}>{c.title}</h3>
                <p className={`leading-relaxed ${t.small}`}>{c.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section ref={ctaRef} className="pb-20 md:pb-28 px-6 bg-white">
        <div className={`max-w-[1288px] mx-auto text-center transition-all duration-700 ${ctaInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Ready to experience the <span className="font-black">difference?</span>
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Join thousands of satisfied users who choose MintSlip for fast, secure, and professional document generation.
          </p>
          <div className="flex justify-center mb-4">
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create Pay Stub Now</span>
            </button>
          </div>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-slate-600 underline underline-offset-4 hover:text-emerald-700 transition-colors"
          >
            View All Documents
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
